import { UserTypes } from '@/types/user.types';
import { LocalesEnum } from '@/enums/locales.enum';
import { UserRoleEnum } from '@/enums/global.enum';
import { AddressTypes } from '@/types/address.types';
import { GoogleTagManagerSubjectEnums } from '@/enums/tracking.enum';
import { useUserStore } from '@/stores/user.store';

import apiService from '@/services/api.service';
import trackingService from '@/services/tracking.service';

interface InviteNewUserPayload {
    email: string;
    first_name: string;
    last_name: string;
    tenant: string;
}

export interface UpdateUserPayload {
    email?: string;
    first_name?: string;
    last_name?: string;
    display_name?: string;
    password?: string;
    old_password?: string;
    phone?: string;
    address?: AddressTypes;
    locale?: LocalesEnum;
    birth_date?: string;
    birth_country?: string;
    captcha_response?: string;
    type?: UserRoleEnum;
    tags?: string[];
    company_name?: string;
    gender?: string;
}

export interface AddNewUserResponse {
    user: UserTypes | undefined;
    error: string;
    alreadyRegistered: boolean;
}

export default {
    async addUser(payload: UpdateUserPayload): Promise<AddNewUserResponse> {
        const { status, data } = await apiService.post('users', payload);

        if (status === 201) {
            trackingService.sendGtm(GoogleTagManagerSubjectEnums.AccountRegistration, {
                email: payload.email,
                phone: payload.phone,
                first_name: payload.first_name,
                last_name: payload.last_name,
            });
        }

        return {
            user: status === 201 ? (data as UserTypes) : undefined,
            error: status !== 201 ? (data as string) : '',
            alreadyRegistered: status === 409,
        };
    },

    async getUser(userId?: string): Promise<UserTypes | undefined> {
        const userStore = useUserStore();
        userId ||= userStore.session.user.id;

        if (!userId) {
            return;
        }

        const { status, data } = await apiService.get(`users/${userId}`);

        if (status === 200) {
            return data as UserTypes;
        }
    },

    async findUserByEmail(email: string, tenant: string): Promise<UserTypes[]> {
        const { status, data } = await apiService.get(`users`, [{ key: 'email', value: email }, { key: 'tenant', value: tenant }]);

        if (status === 200) {
            return data as UserTypes[];
        }

        return [];
    },

    async updateUser(payload: UpdateUserPayload, userId?: string): Promise<UserTypes | undefined> {
        const userStore = useUserStore();
        userId ||= userStore.session.user.id;

        if (!userId || !payload) {
            return;
        }

        const { status, data } = await apiService.post(`users/${userId}`, payload);

        if (status === 200) {
            return data as UserTypes;
        }
    },

    async deleteUser(userId: string): Promise<boolean> {
        const { status } = await apiService.delete(`users/${userId}`);

        return status === 204;
    },

    async invite(payload: InviteNewUserPayload): Promise<any> {
        const { data, status } = await apiService.post('actions/invite', payload);

        if (status === 201) {
            return data;
        }
    },
};
